import { Activity, ChevronRight, Database, Eye, Layout, LayoutGrid, PanelRight, Plus, Workflow } from "lucide-react"
import type { LayerExperience, LayerExperiencePipelineDefinition, LayerExperienceSurfaceDefinition } from "../../types/layer-experience"
import type { MapInteractivityInventoryActionHandlers } from "../../contracts/map-interactivity"
import { Label } from "../../internal/primitives/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../internal/primitives/tabs"
import { cn } from "../../lib/utils"

export interface InteractivitySectionProps extends MapInteractivityInventoryActionHandlers {
  interactivity?: LayerExperience["interactivity"]
}

export function InteractivitySection({
  interactivity,
  onCreateItem,
  onEditItem,
}: InteractivitySectionProps) {
  const surfaceDefinitions = interactivity?.surface_definitions ?? []
  const pipelineDefinitions = interactivity?.pipeline_definitions ?? []

  const handleCreate = (kind: "surface" | "pipeline") => {
    onCreateItem?.(kind)
  }

  const handleEdit = (kind: "surface" | "pipeline", id: string) => {
    onEditItem?.(kind, id)
  }

  return (
    <div className="flex flex-col gap-6 pb-10">
      <div className="flex flex-col gap-1 px-1">
        <Label className="text-[11px] font-bold uppercase text-muted-foreground/70 tracking-wider">Interactions</Label>
      </div>

      <Tabs defaultValue="interactions" className="w-full">
        <div className="px-1 mb-4">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 gap-4 border-b rounded-none">
            <InventoryTabTrigger
              value="interactions"
              label="Interactions"
              count={pipelineDefinitions.length}
              icon={<Workflow className="w-3.5 h-3.5" />}
            />
            <InventoryTabTrigger
              value="surfaces"
              label="Surfaces"
              count={surfaceDefinitions.length}
              icon={<PanelRight className="w-3.5 h-3.5" />}
            />
          </TabsList>
        </div>

        <div className="px-1">
          <TabsContent value="interactions" className="mt-0 focus-visible:ring-0">
            <InventorySectionHeader
              title="Interactions"
              description="Triggered and manually launched pipelines"
              onAdd={() => handleCreate("pipeline")}
              addLabel="Add Interaction"
            />
            <div className="grid gap-2">
              {pipelineDefinitions.map(pipeline => (
                <PipelineCard
                  key={pipeline.pipeline_id}
                  pipeline={pipeline}
                  onEdit={() => handleEdit("pipeline", pipeline.pipeline_id)}
                />
              ))}
              {pipelineDefinitions.length === 0 ? (
                <EmptyState
                  icon={<Workflow className="w-8 h-8" />}
                  title="No interactions defined"
                  description="Create a pipeline for hover, click, context menu, drawing, or manual launch from a surface."
                />
              ) : null}
            </div>
          </TabsContent>

          <TabsContent value="surfaces" className="mt-0 focus-visible:ring-0">
            <InventorySectionHeader
              title="Surfaces"
              description="Reusable panels, sheets, cards, and hover popups"
              onAdd={() => handleCreate("surface")}
              addLabel="Add Surface"
            />
            <div className="grid gap-2">
              {surfaceDefinitions.map(surface => (
                <SurfaceCard
                  key={surface.surface_id}
                  surface={surface}
                  onEdit={() => handleEdit("surface", surface.surface_id)}
                />
              ))}
              {surfaceDefinitions.length === 0 ? (
                <EmptyState
                  icon={<PanelRight className="w-8 h-8" />}
                  title="No surfaces defined"
                  description="Create a side panel, bottom sheet, floating card, or hover popup."
                />
              ) : null}
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  )
}

function InventoryTabTrigger({ value, label, count, icon }: { value: string, label: string, count: number, icon: React.ReactNode }) {
  return (
    <TabsTrigger
      value={value}
      className="bg-transparent border-b-2 border-transparent rounded-none data-[state=active]:bg-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-0 pb-2 flex items-center gap-2 group transition-all"
    >
      <div className="text-muted-foreground group-data-[state=active]:text-primary">
        {icon}
      </div>
      <span className="text-[11px] font-bold tracking-tight uppercase group-data-[state=active]:text-foreground text-muted-foreground">
        {label}
      </span>
      {count > 0 ? (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary transition-colors">
          {count}
        </span>
      ) : null}
    </TabsTrigger>
  )
}

function InventorySectionHeader({ title, description, onAdd, addLabel }: { title: string, description: string, onAdd: () => void, addLabel: string }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex flex-col gap-0.5">
        <Label className="text-[11px] font-bold uppercase text-muted-foreground/70 tracking-wider">{title}</Label>
        <p className="text-[10px] text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="text-[10px] font-bold text-primary flex items-center gap-1 hover:bg-primary/5 px-2 py-1 rounded border border-primary/10 transition-colors"
      >
        <Plus className="w-3 h-3" /> {addLabel}
      </button>
    </div>
  )
}

function EmptyState({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) {
  return (
    <div className="border border-dashed rounded-lg bg-muted/5 flex flex-col items-center justify-center text-center p-6">
      <div className="text-muted-foreground/30 mb-3">{icon}</div>
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <p className="text-[10px] text-muted-foreground/60 mt-1 max-w-[220px]">{description}</p>
    </div>
  )
}

function SurfaceCard({ surface, onEdit }: { surface: LayerExperienceSurfaceDefinition, onEdit: () => void }) {
  return (
    <div
      onClick={onEdit}
      className="group p-3 border rounded-lg bg-card flex items-center gap-3 hover:border-primary/20 transition-all cursor-pointer shadow-sm"
    >
      <div className="p-2 rounded-lg bg-blue-500/10 text-blue-600 border border-blue-500/10">
        <SurfaceIcon kind={surface.surface_kind} className="w-4 h-4" />
      </div>
      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold">{surface.label || surface.surface_id}</span>
          <span className="text-[9px] text-muted-foreground uppercase font-mono tracking-tighter bg-muted px-1.5 rounded font-bold">
            {surface.surface_kind.replace("_", " ")}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <div className="flex items-center gap-1 text-[9px] text-muted-foreground font-medium">
            <LayoutGrid className="w-2.5 h-2.5" />
            {surface.sections.length} Sections
          </div>
          {surface.data_binding ? (
            <div className="flex items-center gap-1 text-[9px] text-primary font-bold uppercase tracking-tighter">
              <Database className="w-2.5 h-2.5" />
              Bound
            </div>
          ) : null}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors" />
    </div>
  )
}

function PipelineCard({ pipeline, onEdit }: { pipeline: LayerExperiencePipelineDefinition, onEdit: () => void }) {
  return (
    <div
      onClick={onEdit}
      className="group p-3 border rounded-lg bg-card flex items-center gap-3 hover:border-emerald-500/30 transition-all cursor-pointer shadow-sm"
    >
      <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-600 border border-emerald-500/10">
        <Workflow className="w-4 h-4" />
      </div>
      <div className="flex flex-1 flex-col min-w-0">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-bold truncate">{pipeline.label || pipeline.pipeline_id}</span>
          <span className="text-[9px] text-emerald-600 font-bold uppercase tracking-tighter bg-emerald-500/5 px-1.5 rounded border border-emerald-500/10">
            {pipeline.trigger.kind.replace("_", " ")}
          </span>
        </div>
        <div className="flex items-center gap-4 mt-1">
          <div className="flex items-center gap-1 text-[9px] text-muted-foreground font-bold">
            <Activity className="w-3 h-3 text-muted-foreground/50" />
            {pipeline.steps.length} Steps
          </div>
          <div className="flex items-center gap-1 text-[9px] text-muted-foreground font-bold">
            <Workflow className="w-3 h-3 text-emerald-500/60" />
            {pipeline.effects.length} Effects
          </div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-emerald-500 transition-colors" />
    </div>
  )
}

function SurfaceIcon({ kind, className }: { kind: string, className?: string }) {
  switch (kind) {
    case "hover_popup":
      return <Eye className={className} />
    case "side_panel":
      return <PanelRight className={className} />
    case "bottom_sheet":
      return <ChevronRight className={cn(className, "rotate-90")} />
    case "floating_summary_card":
      return <LayoutGrid className={className} />
    case "ambiguous_selection_summary":
      return <LayoutGrid className={className} />
    default:
      return <Layout className={className} />
  }
}
